// Shared image-style settings used by Hero, single Image, Image+Text, and
// Background image controls. Keeps the same field names + Tailwind class map
// so the editor UI and the renderer stay in sync.
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";

// Tailwind class maps — kept small and predictable so we don't ship a huge
// bespoke stylesheet for image styling.
export const FIT_CLASS = {
  cover: "object-cover",
  contain: "object-contain",
  fill: "object-fill",
  none: "object-none",
  scale_down: "object-scale-down",
};
export const POS_CLASS = {
  center: "object-center",
  top: "object-top",
  bottom: "object-bottom",
  left: "object-left",
  right: "object-right",
};
export const HEIGHT_CLASS = {
  auto: "h-auto",
  sm: "h-40",
  md: "h-64",
  lg: "h-96",
  xl: "h-[520px]",
  full: "h-full",
};
export const RADIUS_CLASS = {
  none: "rounded-none",
  sm: "rounded",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  full: "rounded-full",
};
export const SHADOW_CLASS = {
  none: "",
  sm: "shadow-sm",
  md: "shadow-md",
  lg: "shadow-lg",
  xl: "shadow-xl",
};

// Compose the className that gets applied to an <img> element based on the
// section data. Callers still add their own layout classes (w-full, block, …).
export function imgStyleClass(d, { defaults = {} } = {}) {
  const fit = d.image_fit ?? defaults.fit ?? "cover";
  const pos = d.image_position_focal ?? defaults.pos ?? "center";
  const height = d.image_height ?? defaults.height ?? "auto";
  const radius = d.image_radius ?? defaults.radius ?? "none";
  const shadow = d.image_shadow ?? defaults.shadow ?? "none";
  return [
    FIT_CLASS[fit] || FIT_CLASS.cover,
    POS_CLASS[pos] || POS_CLASS.center,
    HEIGHT_CLASS[height] || HEIGHT_CLASS.auto,
    RADIUS_CLASS[radius] || RADIUS_CLASS.none,
    SHADOW_CLASS[shadow] || "",
  ].filter(Boolean).join(" ");
}

// Picks the right themed color from a section-data object. Data fields may
// carry a `_dark` variant (e.g. `background_color_dark`).
//
// Dark mode is EXPLICIT-ONLY: if the admin hasn't set an explicit `_dark`
// value we return "" so the caller lets CSS + inherited theme colors take
// over. Otherwise falling back to the light-mode value produces low-
// contrast text (a dark title colour picked for a light bg would render
// dark on a dark bg → invisible).
export function pickThemed(d, key, theme) {
  if (theme === "dark") {
    return d[`${key}_dark`] || "";
  }
  return d[key] || "";
}

export function bgStyleAttrs(d, theme = "light") {
  const attrs = {};
  const bg = pickThemed(d, "background_color", theme);
  if (bg) attrs.backgroundColor = bg;
  if (d.background_image) {
    attrs.backgroundImage = `url(${d.background_image})`;
    attrs.backgroundSize = d.background_fit === "contain" ? "contain" :
                            d.background_fit === "fill" ? "100% 100%" : "cover";
    attrs.backgroundPosition = ({ center: "center", top: "top", bottom: "bottom", left: "left", right: "right" }[d.background_position_focal] || "center");
    attrs.backgroundRepeat = d.background_repeat === "repeat" ? "repeat" : "no-repeat";
  }
  return attrs;
}

// Reusable inline field set — shown inside every image-related editor. `d` is
// the section's data object; `patch(fields)` merges the given fields onto it.
export default function ImageStyleFields({ d, patch, label = "Image style" }) {
  return (
    <div className="border-t border-slate-200 pt-3 mt-1 space-y-3">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Scaling</label>
          <Select value={d.image_fit || "cover"} onValueChange={v => patch({ image_fit: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="cover">Cover — fill space, crop as needed</SelectItem>
              <SelectItem value="contain">Contain — show entire image (no crop)</SelectItem>
              <SelectItem value="fill">Fill — stretch to fit</SelectItem>
              <SelectItem value="scale_down">Scale down — never enlarge</SelectItem>
              <SelectItem value="none">None — original size</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Height</label>
          <Select value={d.image_height || "auto"} onValueChange={v => patch({ image_height: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (natural)</SelectItem>
              <SelectItem value="sm">Small (160px)</SelectItem>
              <SelectItem value="md">Medium (256px)</SelectItem>
              <SelectItem value="lg">Large (384px)</SelectItem>
              <SelectItem value="xl">Extra large (520px)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Focal point</label>
          <Select value={d.image_position_focal || "center"} onValueChange={v => patch({ image_position_focal: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="top">Top</SelectItem>
              <SelectItem value="bottom">Bottom</SelectItem>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Corners</label>
          <Select value={d.image_radius || "none"} onValueChange={v => patch({ image_radius: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Square</SelectItem>
              <SelectItem value="sm">Slight round</SelectItem>
              <SelectItem value="md">Rounded</SelectItem>
              <SelectItem value="lg">Very rounded</SelectItem>
              <SelectItem value="xl">Extra rounded</SelectItem>
              <SelectItem value="full">Circle / pill</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <label className="text-xs uppercase tracking-widest text-slate-500">Shadow</label>
          <Select value={d.image_shadow || "none"} onValueChange={v => patch({ image_shadow: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="sm">Subtle</SelectItem>
              <SelectItem value="md">Soft</SelectItem>
              <SelectItem value="lg">Bold</SelectItem>
              <SelectItem value="xl">Dramatic</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// Extra fields specifically for background images (fit, focal, repeat).
// Used inside DynamicSectionStyle when a background image is set.
export function BackgroundImageStyleFields({ d, patch }) {
  return (
    <div className="grid grid-cols-3 gap-3 pt-2">
      <div>
        <label className="text-xs uppercase tracking-widest text-slate-500">BG scaling</label>
        <Select value={d.background_fit || "cover"} onValueChange={v => patch({ background_fit: v })}>
          <SelectTrigger><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="cover">Cover</SelectItem>
            <SelectItem value="contain">Contain (no crop)</SelectItem>
            <SelectItem value="fill">Fill (stretch)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-slate-500">BG focal</label>
        <Select value={d.background_position_focal || "center"} onValueChange={v => patch({ background_position_focal: v })}>
          <SelectTrigger><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="center">Center</SelectItem>
            <SelectItem value="top">Top</SelectItem>
            <SelectItem value="bottom">Bottom</SelectItem>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-slate-500">BG repeat</label>
        <Select value={d.background_repeat || "no-repeat"} onValueChange={v => patch({ background_repeat: v })}>
          <SelectTrigger><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="no-repeat">Don't repeat</SelectItem>
            <SelectItem value="repeat">Tile / repeat</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
